import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export const commandsFeatureKey = 'commands'

export interface Command {
  command: {
    commandData: unknown
    commandType: string
    notes: string
    accountId: number
  }
  commandId: string
  createdTs: string | Date
  parentCommandId: string
  sessionId: string
  userId: number
  needsReview: boolean
}

export interface HeliosCommandTrace {
  traceId: string
  timestamps: Array<{
    [name: string]: Date | string
  }>
  timestampsFormatted?: Array<{
    stage: string
    timeSincePreviousInMs: number
  }>
  totalTimeInSeconds?: number
}

export type CommandsRedux = {
  commands: Command[]
  recentTrace?: HeliosCommandTrace
  isLoading?: boolean
  approvalRequestSubmitted?: boolean
}

export const commandsInitialState: CommandsRedux = {
  commands: [],
}

export const commandsSlice = createSlice({
  name: commandsFeatureKey,
  initialState: commandsInitialState,
  reducers: {
    setCommands: (
      oldState,
      action: PayloadAction<{ commands: Command[] }>,
    ) => ({
      ...oldState,
      commands: action.payload.commands ?? [],
    }),
    appendCommands: (
      oldState,
      action: PayloadAction<{ commands: Command[] }>,
    ) => ({
      ...oldState,
      commands: [...oldState.commands, ...(action.payload.commands ?? [])],
    }),
    clearCommands: oldState => ({
      ...oldState,
      commands: [],
      pendingCommands: [],
      recentTrace: undefined,
      timedOutCommands: [],
    }),
    clearCommandsByAccountId: (
      oldState,
      action: PayloadAction<{ accountId: number }>,
    ) => ({
      ...oldState,
      commands: oldState.commands.filter(
        c => c.command.accountId !== action.payload.accountId,
      ),
      pendingCommands: [],
      recentTrace: undefined,
      timedOutCommands: [],
    }),
    clearPending: oldState => ({
      ...oldState,
      pendingCommands: [],
    }),
    setTrace: (oldState, action: PayloadAction<HeliosCommandTrace>) => ({
      ...oldState,
      recentTrace: action.payload,
    }),
    setCommandsIsLoading: (
      oldState,
      action: PayloadAction<{ isLoading: boolean }>,
    ) => ({
      ...oldState,
      isLoading: action.payload.isLoading,
    }),
    setApprovalRequestSubmitted: (
      oldState,
      action: PayloadAction<{ approvalRequestSubmitted: boolean }>,
    ) => ({
      ...oldState,
      approvalRequestSubmitted: action.payload.approvalRequestSubmitted,
    }),
  },
})

export const {
  setCommands,
  clearCommands,
  clearCommandsByAccountId,
  clearPending,
  setTrace,
  appendCommands,
  setCommandsIsLoading,
  setApprovalRequestSubmitted,
} = commandsSlice.actions

export default commandsSlice.reducer
